import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { StateService } from '@app/service/state/state.service';
import { RouteComponent } from '../../route.component';
import {
  UserService,
  FinapiPaymentPollResult,
} from '@app/service/user-service/user.service';
import { HeaderService } from '@app/service/header/header.service';
import { Location } from '@angular/common';
import { Subscription } from 'rxjs';
import { delay, filter, repeatWhen, take } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-de-finapi-payment',
  templateUrl: './de-finapi-payment.component.html',
  styleUrls: ['../../page-shared.css', './de-finapi-payment.component.scss'],
})
export class FinAPIPaymentComponent
  extends RouteComponent
  implements OnInit, OnDestroy
{
  loading: boolean = true;
  error: boolean = false;
  webFormId: string;
  navigationInProgress: boolean = false;

  ftsDiv: any;
  headScript: any;
  bodyScript: any;
  pollingSubscription: Subscription;

  constructor(
    protected router: Router,
    protected route: ActivatedRoute,
    protected stateService: StateService,
    protected headerService: HeaderService,
    private userService: UserService,
    private location: Location
  ) {
    super(router, route, stateService, headerService);
  }

  ngOnInit(): void {
    this.setup();
  }

  ngOnDestroy(): void {
    this.removeScript();
  }

  async setup(): Promise<void> {
    await this.attemptToFetchFinapiPaymentWebform();
    this.loading = false;
  }

  async attemptToFetchFinapiPaymentWebform(): Promise<void> {
    this.pollingSubscription = this.userService
      .startFinapiStandalonePayment()
      .pipe(
        repeatWhen((obs) => obs.pipe(delay(1000))),
        take(120),
        filter(
          (res: FinapiPaymentPollResult) => !!res?.webformId || res?.success
        ),
      )
      .subscribe((response: FinapiPaymentPollResult) => {
        this.handlePaymentPollResult(response);
      });
  }

  async handlePaymentPollResult(
    result: FinapiPaymentPollResult
  ): Promise<void> {
    if (result.webformId) {
      this.webFormId = result.webformId;
      this.stopPolling();
      await this.loadFinApiHeadScript(result.webformId);
      this.loading = false;
    } else {
      this.navigate();
    }
  }

  removeScript(): void {
    this.headScript?.bodyScript?.remove();
    this.headScript?.remove();
  }

  async loadFinApiHeadScript(token: string): Promise<void> {
    this.removeScript();

    this.headScript = document.createElement('script');
    this.headScript.src =
      'https://webform-dist.finapi.io/latest/dist/web-form.min.js';
    this.headScript.type = 'text/javascript';

    this.headScript.addEventListener('load', () => {
      this.bodyScript = document.createElement('script');
      this.bodyScript.type = 'text/javascript';

      this.bodyScript.textContent = `
        (async () => {
          FinApiWebForm.load(
            document.querySelector("#webFormContainer"),
            {
              token: "${token}",
              targetUrl: "${environment.finApiUrl}",
              layoutConfig: "xs",
              customerSupportUrl: "https://zaver.com/de/customer-support",
              language: "de",
            },
            {
              onLoaded: function() {
                window.dispatchEvent(new CustomEvent('loaded'));
              },
              onComplete: function() {
                window.dispatchEvent(new CustomEvent('finish'));
              },
              onFail: function() {
                window.dispatchEvent(new CustomEvent('error'));
              },
              onAbort: function() {
                window.dispatchEvent(new CustomEvent('abort'));
              },
              onLoadError: function() {
                window.dispatchEvent(new CustomEvent('error'));
              }
            }
          );
        })();
      `;

      document.getElementsByTagName('body')[0].appendChild(this.bodyScript);
    });

    document.getElementsByTagName('head')[0].appendChild(this.headScript);
  }

  @HostListener('window:error', ['$event'])
  logError(e: CustomEvent) {
    this.attemptToFetchFinapiPaymentWebform();
  }

  @HostListener('window:finish')
  finishSuccessfulWizard() {
    this.loading = true;
    this.pollForPaymentResult();
  }

  @HostListener('window:abort')
  abortUnsuccessfulWizard() {
    this.attemptToFetchFinapiPaymentWebform();
  }

  @HostListener('window:loaded')
  loaded() {
    this.loading = false;
  }

  async handleCtaClick(): Promise<void> {
    this.navigationInProgress = true;
    this.location.back();
    this.navigationInProgress = false;
  }

  pollForPaymentResult(): void {
    this.pollingSubscription = this.userService
      .pollForFinapiPaymentWebform(this.webFormId)
      .pipe(
        repeatWhen((obs) => obs.pipe(delay(1000))),
        take(120),
        filter((res) => res)
      )
      .subscribe(() => this.handleFinishedPayment());
  }

  private stopPolling(): void {
    if (this.pollingSubscription) {
      this.pollingSubscription.unsubscribe();
    }
  }

  async handleFinishedPayment(): Promise<void> {
    this.stopPolling();
    this.navigate();
  }
}
